import { useEffect, useState } from "react";
import api from "../api";

export default function TwitchBot() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [viewsError, setViewsError] = useState("");
  const [viewerItems, setViewerItems] = useState([]);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [hideBots, setHideBots] = useState(true);
  const [extraBots, setExtraBots] = useState("");
  const [expanded, setExpanded] = useState({}); // key -> bool
  const [chatSource, setChatSource] = useState('auto'); // 'auto' | 'tmi' | 'helix'
  const [oauthStatus, setOauthStatus] = useState({ connected: false, login: '', userId: '', scopes: [], expiresAt: null });
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthMsg, setOauthMsg] = useState('');
  const [awardPoints, setAwardPoints] = useState(5);
  const [lastAwards, setLastAwards] = useState({ awards: 0, lastRunAt: null, loading: false, error: '' });
  // Listen live chat session
  const [listenMinutes, setListenMinutes] = useState(5);
  const [listenSessionId, setListenSessionId] = useState(null);
  const [listenEndsAt, setListenEndsAt] = useState(null);
  const [listenRunning, setListenRunning] = useState(false);
  const [listenTimeLeftMs, setListenTimeLeftMs] = useState(0);
  const [listenPartial, setListenPartial] = useState([]);
  const [listenPartialByChannel, setListenPartialByChannel] = useState([]);
  const [listenResult, setListenResult] = useState(null);
  const [listenResultByChannel, setListenResultByChannel] = useState([]);
  const [listenError, setListenError] = useState('');
  const [listenTick, setListenTick] = useState(0); // for simple animation

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get('/admin/live-settings');
        if (!mounted) return;
        setEnabled(!!data.twitchBotEnabled);
        if (typeof data.twitchLurkPoints === 'number') setAwardPoints(data.twitchLurkPoints);
        // OAuth status
        try {
          const r = await api.get('/admin/twitch-bot/oauth/status');
          if (mounted) setOauthStatus(r.data || { connected:false });
        } catch {}
        // Last awards
        try {
          const r2 = await api.get('/admin/twitch-bot/last-awards');
          if (mounted) setLastAwards(prev => ({ ...prev, awards: Number(r2.data?.awards||0), lastRunAt: r2.data?.finishedAt || r2.data?.lastRunAt || null }));
        } catch {}
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.error || e?.message || 'Fehler beim Laden.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleToggle = async (checked) => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.put('/admin/live-settings', { twitchBotEnabled: !!checked });
      setEnabled(!!data.twitchBotEnabled);
      setSuccess('Einstellung gespeichert.');
      setTimeout(() => setSuccess(""), 1500);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Konnte nicht speichern.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAwardPoints = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.put('/admin/live-settings', { twitchLurkPoints: Number.isFinite(Number(awardPoints)) ? parseInt(awardPoints,10) : 5 });
      setAwardPoints(typeof data.twitchLurkPoints === 'number' ? data.twitchLurkPoints : 5);
      setSuccess('Punktewert gespeichert.');
      setTimeout(() => setSuccess(""), 1500);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Konnte Punktewert nicht speichern.');
    } finally {
      setSaving(false);
    }
  };

  const refreshLastAwards = async () => {
    setLastAwards(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const r = await api.get('/admin/twitch-bot/last-awards');
      setLastAwards({ awards: Number(r.data?.awards||0), lastRunAt: r.data?.finishedAt || r.data?.lastRunAt || null, loading: false, error: '' });
    } catch (e) {
      setLastAwards(prev => ({ ...prev, loading: false, error: e?.response?.data?.error || e?.message || 'Fehler beim Laden.' }));
    }
  };

  const startOAuth = () => {
    // Force using the origin + /api/integrations to avoid SPA 404s behind reverse proxies
    try {
      const origin = new URL(api?.defaults?.baseURL || window.location.origin).origin;
      const url = `${origin}/api/integrations/twitch/start?mode=global-chatters`;
      window.location.href = url;
    } catch {
      const url = `/api/integrations/twitch/start?mode=global-chatters`;
      window.location.href = url;
    }
  };

  const revokeOAuth = async () => {
    setOauthLoading(true);
    setOauthMsg('');
    try {
      await api.post('/admin/twitch-bot/oauth/revoke');
      const r = await api.get('/admin/twitch-bot/oauth/status');
      setOauthStatus(r.data || { connected:false });
      setOauthMsg('Verknüpfung entfernt.');
      setTimeout(() => setOauthMsg(''), 1500);
    } catch (e) {
      setOauthMsg(e?.response?.data?.error || e?.message || 'Konnte Token nicht löschen.');
    } finally {
      setOauthLoading(false);
    }
  };

  const refreshOAuthStatus = async () => {
    setOauthLoading(true);
    setOauthMsg('Aktualisiere …');
    try {
      const r = await api.get('/admin/twitch-bot/oauth/status');
      setOauthStatus(r.data || { connected:false });
      setOauthMsg('Status aktualisiert.');
      setTimeout(() => setOauthMsg(''), 1200);
    } catch (e) {
      setOauthMsg(e?.response?.data?.error || e?.message || 'Status konnte nicht geladen werden.');
    } finally {
      setOauthLoading(false);
    }
  };

  // Handle redirect result ?oauth=ok|error
  useEffect(() => {
    const u = new URL(window.location.href);
    const status = u.searchParams.get('oauth');
    if (status === 'ok' || status === 'error') {
      // clean URL
      u.searchParams.delete('oauth');
      window.history.replaceState({}, document.title, u.pathname + u.search);
      // reload status
      (async () => {
        try { const r = await api.get('/admin/twitch-bot/oauth/status'); setOauthStatus(r.data || { connected:false }); }
        catch {}
        if (status === 'ok') setOauthMsg('Twitch erfolgreich verbunden.');
        else setOauthMsg('Twitch-Verknüpfung fehlgeschlagen.');
        setTimeout(() => setOauthMsg(''), 2500);
      })();
    }
  }, []);

  const fetchActiveViewers = async () => {
    setViewsLoading(true);
    setViewsError("");
    try {
      const params = {};
      if (chatSource !== 'auto') params.chatSource = chatSource;
      const { data } = await api.get('/admin/twitch-bot/active-viewers', { params });
      const items = Array.isArray(data?.entries) ? data.entries : [];
      setViewerItems(items);
      setLastFetchedAt(new Date());
    } catch (e) {
      setViewsError(e?.response?.data?.error || e?.message || 'Fehler beim Abfragen der Zuschauer.');
      setViewerItems([]);
    } finally {
      setViewsLoading(false);
    }
  };

  // Poll status while listening
  useEffect(() => {
    if (!listenRunning || !listenSessionId) return; 
    setListenError('');
    const id = setInterval(async () => {
      try {
        const { data } = await api.get('/admin/twitch-bot/listen/status', { params: { sessionId: listenSessionId } });
        setListenEndsAt(data.endsAt ? new Date(data.endsAt) : null);
        setListenTimeLeftMs(Number(data.timeLeftMs || 0));
        setListenPartial(Array.isArray(data.partial) ? data.partial : []);
        setListenPartialByChannel(Array.isArray(data.partialByChannel) ? data.partialByChannel : []);
        setListenRunning(!!data.running);
        setListenTick(t => (t+1)%4);
        if (!data.running || Number(data.timeLeftMs||0) <= 0) {
          clearInterval(id);
          // fetch result
          try {
            const r = await api.get('/admin/twitch-bot/listen/result', { params: { sessionId: listenSessionId } });
            setListenResult(r.data || {});
            setListenResultByChannel(Array.isArray(r.data?.resultByChannel) ? r.data.resultByChannel : []);
          } catch (e) {
            setListenError(e?.response?.data?.error || e?.message || 'Fehler beim Laden des Ergebnisses.');
          }
        }
      } catch (e) {
        clearInterval(id);
        setListenError(e?.response?.data?.error || e?.message || 'Fehler beim Status.');
        setListenRunning(false);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [listenRunning, listenSessionId]);

  const startListen = async () => {
    setListenError('');
    setListenResult(null);
    setListenPartial([]);
    try {
      const minutes = Math.max(1, Math.min(60, parseInt(listenMinutes || 5, 10)));
      const { data } = await api.post('/admin/twitch-bot/listen/start', { minutes });
      if (data.immediate) {
        // No active channels -> empty result
        setListenRunning(false);
        setListenSessionId(data.sessionId || null);
        setListenEndsAt(data.endsAt ? new Date(data.endsAt) : null);
        try {
          const r = await api.get('/admin/twitch-bot/listen/result', { params: { sessionId: data.sessionId } });
          setListenResult(r.data || {});
          setListenResultByChannel(Array.isArray(r.data?.resultByChannel) ? r.data.resultByChannel : []);
        } catch (e) {
          setListenError(e?.response?.data?.error || e?.message || 'Keine aktiven Streams.');
        }
        return;
      }
      setListenSessionId(data.sessionId || null);
      setListenEndsAt(data.endsAt ? new Date(data.endsAt) : null);
      setListenRunning(true);
      setListenTimeLeftMs(((data.endsAt ? new Date(data.endsAt) : new Date()).getTime()) - Date.now());
      setListenTick(0);
    } catch (e) {
      setListenError(e?.response?.data?.error || e?.message || 'Konnte Live‑Mitlesen nicht starten.');
      setListenRunning(false);
    }
  };

  function fmtTimeLeft(ms) {
    const s = Math.max(0, Math.floor(ms/1000));
    const m = Math.floor(s/60);
    const r = s%60;
    return `${String(m).padStart(2,'0')}:${String(r).padStart(2,'0')}`;
  }

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Twitch‑Bot</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>Steuere hier die Aktivierung des Twitch‑Bots. Weitere Optionen folgen.</p>
      </section>
      <section style={{ width:'100%', maxWidth:780, margin:'16px auto 40px', padding:16 }}>
        {error && (
          <div style={{color:'#ff6b6b', background:'rgba(255,107,107,0.1)', border:'1px solid rgba(255,107,107,0.35)', padding:'10px 12px', borderRadius:8, marginBottom:12}}>{error}</div>
        )}
        {success && (
          <div style={{color:'#2ecc71', background:'rgba(46,204,113,0.08)', border:'1px solid rgba(46,204,113,0.35)', padding:'10px 12px', borderRadius:8, marginBottom:12}}>{success}</div>
        )}
        <div style={{display:'flex', alignItems:'center', gap:16, padding:16, background:'rgba(0,0,0,0.08)', border:'1px solid #222', borderRadius:12}}>
          <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer'}}>
            <input type="checkbox" disabled={loading||saving} checked={!!enabled} onChange={e => handleToggle(e.target.checked)} />
            <span style={{fontWeight:700}}>Twitch‑Bot aktivieren</span>
          </label>
          {loading && <span style={{opacity:.7}}>Lädt …</span>}
          {saving && <span style={{opacity:.7}}>Speichert …</span>}
          <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:8}}>
            <span style={{fontSize:13, opacity:.9}}>Lurk‑Punkte pro Match:</span>
            <input
              type="number"
              min={0}
              value={awardPoints}
              onChange={e => setAwardPoints(e.target.value)}
              style={{width:80, padding:'6px 8px', borderRadius:6, border:'1px solid #333', background:'#0b0f14', color:'#dfe7ef'}}
            />
            <button onClick={handleSaveAwardPoints} disabled={saving} style={{padding:'6px 10px', border:'1px solid #345', background:'#245', color:'#fff', borderRadius:6}}>Speichern</button>
          </div>
        </div>
        <div style={{marginTop:14, fontSize:14, color:'#cde7ff'}}>
          Hinweis: Diese Einstellung ist global. Der Bot reagiert nur, wenn die Integration korrekt eingerichtet ist.
        </div>

        <hr style={{borderColor:'#333', margin:'24px 0'}} />
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <button
            onClick={fetchActiveViewers}
            disabled={!enabled || viewsLoading || loading || saving}
            style={{
              opacity: (!enabled || viewsLoading || loading || saving) ? 0.6 : 1,
              cursor: (!enabled || viewsLoading || loading || saving) ? 'not-allowed' : 'pointer',
              padding:'10px 14px', borderRadius:8, border:'1px solid #2b5', background:'#184', color:'#fff', fontWeight:700
            }}
          >
            {viewsLoading ? 'Frage Zuschauer ab …' : 'Aktuelle Twitch‑Zuschauer anzeigen'}
          </button>
          <div title={lastAwards.lastRunAt ? `Letzter Lauf: ${new Date(lastAwards.lastRunAt).toLocaleString()}` : 'Noch keine Daten'} style={{fontSize:12, padding:'4px 8px', border:'1px solid #234', background:'#0c1620', color:'#9fd', borderRadius:999}}>
            Letzte Vergaben: <b>+{Number(lastAwards.awards||0)}</b>
          </div>
          <button onClick={refreshLastAwards} disabled={lastAwards.loading} style={{padding:'6px 10px', border:'1px solid #334', background:'#123', color:'#9fd', borderRadius:6}}>
            {lastAwards.loading ? 'Lädt…' : 'Aktualisieren'}
          </button>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <span style={{fontSize:13, opacity:.9}}>Quelle:</span>
            <select value={chatSource} onChange={e => setChatSource(e.target.value)} style={{padding:'6px 8px', borderRadius:6, border:'1px solid #333', background:'#0b0f14', color:'#dfe7ef'}}>
              <option value="auto">Automatisch</option>
              <option value="tmi">TMI (Community‑Liste)</option>
              <option value="helix">Helix (OAuth)</option>
            </select>
          </div>
          {!enabled && <span style={{fontSize:13, color:'#ffc'}}>Aktiviere zuerst den Twitch‑Bot, um diese Aktion zu nutzen.</span>}
        </div>

        {/* Live Chat Mitlesen */}
        <div style={{marginTop:16, padding:12, border:'1px solid #223', borderRadius:8, background:'rgba(0,0,0,0.05)'}}>
          <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
            <div style={{fontWeight:700}}>Live‑Chat mitlesen (anonym)</div>
            <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:8}}>
              <span style={{fontSize:13, opacity:.9}}>Dauer (Minuten):</span>
              <input type="number" min={1} max={60} value={listenMinutes} onChange={e => setListenMinutes(e.target.value)} style={{width:80, padding:'6px 8px', borderRadius:6, border:'1px solid #333', background:'#0b0f14', color:'#dfe7ef'}} />
              <button onClick={startListen} disabled={!enabled || listenRunning} style={{padding:'8px 12px', border:'1px solid #2b5', background: listenRunning ? '#274' : '#184', color:'#fff', borderRadius:6, fontWeight:700}}>
                {listenRunning ? 'Läuft …' : 'Mitlesen starten'}
              </button>
            </div>
          </div>
          {listenError && (
            <div style={{marginTop:8, color:'#ff6b6b'}}>{listenError}</div>
          )}
          {listenRunning && (
            <div style={{marginTop:10, display:'flex', alignItems:'center', gap:12}}>
              <div style={{width:12, height:12, borderRadius:'50%', background:'#7fd', animation:'pulse 1s infinite', boxShadow:'0 0 0 0 rgba(127,253,255,0.7)'}} />
              <style>{`@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(127,253,255,0.7)}70%{box-shadow:0 0 0 10px rgba(127,253,255,0)}100%{box-shadow:0 0 0 0 rgba(127,253,255,0)}}`}</style>
              <div style={{color:'#9fd'}}>Mitlesen läuft … Restzeit: <b>{fmtTimeLeft(listenTimeLeftMs)}</b> {'.'.repeat(listenTick)}</div>
            </div>
          )}
          {listenRunning && Array.isArray(listenPartial) && listenPartial.length > 0 && (
            <div style={{marginTop:10}}>
              <div style={{fontSize:13, opacity:.9, marginBottom:6}}>Zwischenstand (Top 10):</div>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
                <thead>
                  <tr><th style={{textAlign:'left', borderBottom:'1px solid #233', padding:'4px 6px'}}>Name</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'4px 6px'}}>Nachrichten</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'4px 6px'}}>Wörter</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'4px 6px'}}>Zeichen</th></tr>
                </thead>
                <tbody>
                  {listenPartial.slice(0,10).map((u, idx) => (
                    <tr key={u.login+idx}>
                      <td style={{padding:'4px 6px'}}>{u.displayName || u.login}</td>
                      <td style={{padding:'4px 6px', textAlign:'right'}}>{u.messages}</td>
                      <td style={{padding:'4px 6px', textAlign:'right'}}>{u.words}</td>
                      <td style={{padding:'4px 6px', textAlign:'right'}}>{u.chars}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Per-channel partials */}
              {Array.isArray(listenPartialByChannel) && listenPartialByChannel.length > 0 && (
                <div style={{marginTop:12}}>
                  <div style={{fontSize:13, opacity:.9, marginBottom:6}}>Zwischenstand pro Kanal:</div>
                  {listenPartialByChannel.map((pc, i) => (
                    <div key={pc.channel+String(i)} style={{marginBottom:10}}>
                      <div style={{fontSize:13, color:'#9fd', margin:'6px 0'}}># {pc.channel}</div>
                      <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
                        <thead>
                          <tr><th style={{textAlign:'left', borderBottom:'1px solid #233', padding:'4px 6px'}}>Name</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'4px 6px'}}>Nachrichten</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'4px 6px'}}>Wörter</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'4px 6px'}}>Zeichen</th></tr>
                        </thead>
                        <tbody>
                          {(pc.result||[]).slice(0,10).map((u, idx) => (
                            <tr key={u.login+idx}>
                              <td style={{padding:'4px 6px'}}>{u.displayName || u.login}</td>
                              <td style={{padding:'4px 6px', textAlign:'right'}}>{u.messages}</td>
                              <td style={{padding:'4px 6px', textAlign:'right'}}>{u.words}</td>
                              <td style={{padding:'4px 6px', textAlign:'right'}}>{u.chars}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {listenResult && Array.isArray(listenResult.result) && (
            <div style={{marginTop:12}}>
              <div style={{fontWeight:700, marginBottom:6}}>Ergebnis</div>
              <div style={{fontSize:13, opacity:.9, marginBottom:6}}>
                Zeitraum: {listenResult.startedAt ? new Date(listenResult.startedAt).toLocaleTimeString() : '—'} – {listenResult.finishedAt ? new Date(listenResult.finishedAt).toLocaleTimeString() : '—'} • Kanäle: {Array.isArray(listenResult.channels) ? listenResult.channels.join(', ') : '—'}
              </div>
              {listenResult.result.length === 0 ? (
                <div style={{color:'#cde7ff'}}>Keine aktiven Chat‑Nachrichten in diesem Zeitraum.</div>
              ) : (
                <table style={{width:'100%', borderCollapse:'collapse'}}>
                  <thead>
                    <tr><th style={{textAlign:'left', borderBottom:'1px solid #233', padding:'6px'}}>Name</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'6px'}}>Nachrichten</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'6px'}}>Wörter</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'6px'}}>Zeichen</th></tr>
                  </thead>
                  <tbody>
                    {listenResult.result.map((u, idx) => (
                      <tr key={u.login+idx}>
                        <td style={{padding:'6px'}}>{u.displayName || u.login}</td>
                        <td style={{padding:'6px', textAlign:'right'}}>{u.messages}</td>
                        <td style={{padding:'6px', textAlign:'right'}}>{u.words}</td>
                        <td style={{padding:'6px', textAlign:'right'}}>{u.chars}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {/* Per-channel final results */}
              {Array.isArray(listenResultByChannel) && listenResultByChannel.length > 0 && (
                <div style={{marginTop:16}}>
                  <div style={{fontWeight:700, marginBottom:6}}>Ergebnis pro Kanal</div>
                  {listenResultByChannel.map((rc, i) => (
                    <div key={rc.channel+String(i)} style={{marginBottom:16}}>
                      <div style={{fontSize:13, color:'#9fd', margin:'6px 0'}}># {rc.channel}</div>
                      {(rc.result||[]).length === 0 ? (
                        <div style={{color:'#cde7ff'}}>Keine Nachrichten in diesem Kanal.</div>
                      ) : (
                        <table style={{width:'100%', borderCollapse:'collapse'}}>
                          <thead>
                            <tr><th style={{textAlign:'left', borderBottom:'1px solid #233', padding:'6px'}}>Name</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'6px'}}>Nachrichten</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'6px'}}>Wörter</th><th style={{textAlign:'right', borderBottom:'1px solid #233', padding:'6px'}}>Zeichen</th></tr>
                          </thead>
                          <tbody>
                            {rc.result.map((u, idx) => (
                              <tr key={u.login+idx}>
                                <td style={{padding:'6px'}}>{u.displayName || u.login}</td>
                                <td style={{padding:'6px', textAlign:'right'}}>{u.messages}</td>
                                <td style={{padding:'6px', textAlign:'right'}}>{u.words}</td>
                                <td style={{padding:'6px', textAlign:'right'}}>{u.chars}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{marginTop:16, padding:12, border:'1px solid #223', borderRadius:8, background:'rgba(0,0,0,0.05)'}}>
          <div style={{fontWeight:700, marginBottom:6}}>Globaler Twitch‑OAuth für Chatters</div>
          <div style={{fontSize:13, opacity:.9, marginBottom:8}}>
            Verbinde hier einen Twitch‑Account (z. B. Moderator‑Bot), der die Berechtigung "moderator:read:chatters" hat. Dieser wird für die Chatternamen‑Abfrage genutzt.
          </div>
          {oauthMsg && (
            <div style={{marginBottom:8, color:'#9fd'}}>{oauthMsg}</div>
          )}
          <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
            <div>
              Status: {oauthStatus.connected ? (
                <span style={{color:'#2ecc71', fontWeight:700}}>verbunden</span>
              ) : (
                <span style={{color:'#ff6b6b', fontWeight:700}}>nicht verbunden</span>
              )}
            </div>
            {oauthStatus.connected && (
              <div style={{fontSize:13, opacity:.9}}>
                Login: <b>{oauthStatus.login || '—'}</b>{' '}
                {oauthStatus.expiresAt ? <span>• Läuft ab: {new Date(oauthStatus.expiresAt).toLocaleString()}</span> : null}
              </div>
            )}
            <div style={{marginLeft:'auto', display:'flex', gap:8, alignItems:'center'}}>
              <button onClick={refreshOAuthStatus} disabled={oauthLoading} style={{padding:'8px 12px', border:'1px solid #334', background:'#123', color:'#9fd', borderRadius:6, fontWeight:700}}>
                {oauthLoading ? 'Lädt …' : 'Aktualisieren'}
              </button>
              {!oauthStatus.connected ? (
                <button onClick={startOAuth} disabled={oauthLoading} style={{padding:'8px 12px', border:'1px solid #445', background:'#1b4', color:'#fff', borderRadius:6, fontWeight:700}}>
                  Mit Twitch verbinden
                </button>
              ) : (
                <button onClick={revokeOAuth} disabled={oauthLoading} style={{padding:'8px 12px', border:'1px solid #554', background:'#822', color:'#fff', borderRadius:6, fontWeight:700}}>
                  Verknüpfung entfernen
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center', marginTop:12}}>
          <label style={{display:'flex', alignItems:'center', gap:8}}>
            <input type="checkbox" checked={hideBots} onChange={e => setHideBots(e.target.checked)} />
            <span>Bots ausblenden</span>
          </label>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <span style={{fontSize:13, opacity:.85}}>Zusätzliche Bot‑Namen (Kommagetrennt):</span>
            <input
              value={extraBots}
              onChange={e => setExtraBots(e.target.value)}
              placeholder="z. B. Nightbot, Streamlabs"
              style={{padding:'6px 8px', borderRadius:6, border:'1px solid #333', background:'#0b0f14', color:'#dfe7ef', minWidth:280}}
            />
          </div>
        </div>

        {viewsError && (
          <div style={{marginTop:12, color:'#ff6b6b', background:'rgba(255,107,107,0.1)', border:'1px solid rgba(255,107,107,0.35)', padding:'10px 12px', borderRadius:8}}>
            {viewsError}
          </div>
        )}

        {viewerItems.length > 0 && (
          <div style={{marginTop:16}}>
            <div style={{marginBottom:8, color:'#9fd', fontSize:14}}>
              Ergebnisse{lastFetchedAt ? ` — Stand: ${lastFetchedAt.toLocaleTimeString()}` : ''}
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr auto', gap:8, border:'1px solid #222', borderRadius:8}}>
              {viewerItems.map(item => {
                const baseBots = ['moobot','nightbot','streamelements','streamelements','fossabot','wizebot','streamlabs'];
                const extra = extraBots.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                const botSet = new Set([...baseBots, ...extra]);
                const names = Array.isArray(item.chatters) ? item.chatters : [];
                const filtered = hideBots ? names.filter(n => !botSet.has(String(n||'').trim().toLowerCase())) : names;
                const total = Number(item.chattersTotal ?? names.length ?? 0);
                const key = `${item.entryId}-${item.twitchLogin}`;
                const isExpanded = !!expanded[key];
                const shown = isExpanded ? filtered : filtered.slice(0, 50);
                return (
                <div key={`${item.entryId}-${item.twitchLogin}`} style={{display:'contents'}}>
                  <div style={{padding:'10px 12px', borderBottom:'1px solid #222'}}>
                    <div style={{fontWeight:700}}>{item.userDisplayName || item.twitchLogin}</div>
                    <div style={{fontSize:13, opacity:.8}}>
                      <a href={item.streamLink} target="_blank" rel="noreferrer" style={{color:'#8fd'}}>
                        twitch.tv/{item.twitchLogin}
                      </a>
                      {item.streamTitle ? <> — <span title="Stream-Titel">{item.streamTitle}</span></> : null}
                      {item.chattersSource ? (
                        <>
                          {' '}• Quelle: <span style={{color:'#9fd'}}>{item.chattersSource.toUpperCase()}</span>
                          {item.chattersSource === 'helix' && item.oauthMethod ? (
                            <>
                              {' '}• OAuth: {item.oauthMethod === 'broadcaster' ? 'Broadcaster' : item.oauthMethod === 'moderator' ? 'Moderator' : item.oauthMethod === 'global-moderator' ? 'Global‑Moderator' : '—'}{item.oauthLoginUsed ? ` ${item.oauthLoginUsed}` : ''}
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                    {(shown.length > 0 || total > 0) ? (
                      <div style={{marginTop:8, fontSize:13, color:'#cde7ff'}}>
                        Chatternamen ({isExpanded ? filtered.length : Math.min(filtered.length, 50)}{total > filtered.length ? ` von ${total}` : ''}):{' '}
                        {shown.join(', ')}
                        {!isExpanded && filtered.length > 50 ? (
                          <>
                            {' '}…{' '}
                            <button
                              onClick={() => setExpanded(prev => ({ ...prev, [key]: true }))}
                              style={{ background:'transparent', color:'#9fd', border:'none', textDecoration:'underline', cursor:'pointer' }}
                            >
                              Alle anzeigen
                            </button>
                          </>
                        ) : null}
                        {isExpanded && filtered.length > 50 ? (
                          <>
                            {' '}
                            <button
                              onClick={() => setExpanded(prev => ({ ...prev, [key]: false }))}
                              style={{ background:'transparent', color:'#9fd', border:'none', textDecoration:'underline', cursor:'pointer' }}
                            >
                              Weniger anzeigen
                            </button>
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <div style={{marginTop:8, fontSize:13, color:'#b9c6d0', opacity:.85}}>
                        Keine Chatternamen verfügbar (Broadcaster‑OAuth erforderlich).
                      </div>
                    )}
                    {Array.isArray(item.matchedMembers) && item.matchedMembers.length > 0 ? (
                      <div style={{marginTop:8, fontSize:13, color:'#cde7ff'}}>
                        Zugeordnete Mitglieder ({item.matchedMembers.length}):{' '}
                        {item.matchedMembers.map((m, idx) => (
                          <span key={`${m.userId}-${idx}`}>
                            <a href={`/u/${m.userId}`} target="_blank" rel="noreferrer" style={{color:'#9fd'}}>{m.displayName || m.username}</a>
                            {m.chatter ? ` (@${m.chatter})` : ''}
                            {m.matchedBy ? ` – Quelle: ${m.matchedBy === 'twitchName' ? 'Profil (Twitch‑Name)' : 'DisplayName'}` : ''}
                            {idx < item.matchedMembers.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div style={{padding:'10px 12px', textAlign:'right', borderBottom:'1px solid #222', fontWeight:700}}>
                    {typeof item.viewerCount === 'number' ? `${item.viewerCount} Zuschauer` : '—'}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
